import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { showToast } from '@/globalFunction';
import { useToggle } from '@/hooks/useToggle';
import {
  checkAccess,
  MASTER_ADMIN_ROLES,
} from '@/lib/authorization/authorization';
import { queryClient } from '@/lib/react-query/react-query';
import { RootState } from '@/reducers/types';
import { parseAndSanitizeHtml } from '@/util/html';

// import Checkbox from '../../common/Elements/Checkbox/Checkbox';
import { ShadowRoot } from '../../common/Elements/ShadowRoot/ShadowRoot';
import {
  privacyPolicyQueryKey,
  useGetPrivacyPolicy,
} from '../api/getPrivacyPolicy';
import { useUpdatePrivacyPolicy } from '../api/updatePrivacyPolicy';
import Editor from '../Elements/Editor';
import EditorLayout from '../Elements/EditorLayout';
import MainLayout from '../Elements/MainLayout';
import { useConfirmSaveLegalDocument } from '../hooks';

const PrivacyPolicy = ({ isView = false }) => {
  const [editorContent, setEditorContent] = useState(null);
  // const [isReset, toggleIsReset] = useToggle(false);
  const [isEditMode, toggleIsEditMode] = useToggle(false);
  const editorRef = useRef(null);

  const privacyPolicyQuery = useGetPrivacyPolicy({});
  const updatePrivacyPolicyMutation = useUpdatePrivacyPolicy();
  const [ConfirmSaveLegalDocumentModal, confirmSaveLegalDocument] =
    useConfirmSaveLegalDocument();

  useEffect(() => {
    if (!privacyPolicyQuery.data) return;

    setEditorContent(privacyPolicyQuery.data.privacyPolicy?.content);
  }, [privacyPolicyQuery.data]);

  const handleClickSave = async () => {
    if (!(await confirmSaveLegalDocument())) return;

    const editorContent = editorRef.current.getContent({ format: 'raw' });

    try {
      await updatePrivacyPolicyMutation.mutateAsync({
        content: editorContent,
      });

      queryClient.setQueryData(privacyPolicyQueryKey, (prev: any) => {
        return {
          ...prev,
          privacyPolicy: {
            ...prev.privacyPolicy,
            content: editorContent,
          },
        };
      });

      showToast('success', 'Changes successsfully saved!');
      toggleIsEditMode();
    } catch (error) {
      showToast(
        'error',
        error.response.data?.message ??
          'Something went wrong please try again later.'
      );
    }
  };

  // const handleCheckReset = () => {
  //   toggleIsReset();
  // };

  const loggedUser = useSelector((state: RootState) => state.loggedUser.data);

  const isMasterAdmin = useMemo(
    () => checkAccess({ user: loggedUser, allowedRoles: MASTER_ADMIN_ROLES }),
    [loggedUser]
  );

  const isEditable = useMemo(
    () => isMasterAdmin && !isView,
    [isMasterAdmin, isView]
  );

  const htmlView = useMemo(
    () => (editorContent ? parseAndSanitizeHtml(editorContent) : null),
    [editorContent]
  );

  return (
    <>
      <MainLayout>
        <EditorLayout
          title="Privacy Policy"
          isEditMode={isEditMode}
          toggleIsEditMode={toggleIsEditMode}
          onSave={handleClickSave}
          isEditable={isEditable}
        >
          {isEditMode && isEditable && (
            <div className="tw-flex tw-flex-col tw-border tw-border-cfo-grey-500 tw-rounded-b-[10px] tw-h-full">
              <div className="legal-information-editor-container tw-h-full">
                <Editor initialValue={editorContent} editorRef={editorRef} />
              </div>
              {/* <div className="tw-flex tw-justify-end tw-gap-[5px] tw-p-[20px] tw-border-t tw-border-cfo-grey-500">
              <Checkbox checked={isReset} onChange={handleCheckReset} />
              <span
                className="tw-font-brandonTextMedium hover:tw-cursor-pointer"
                onClick={handleCheckReset}
                onKeyUp={handleCheckReset}
                tabIndex={0}
                role="button"
              >
                Reset Privacy Policy?
              </span>
            </div> */}
            </div>
          )}

          {!isEditMode && htmlView && (
            <div className="">
              <ShadowRoot htmlView={htmlView} />
            </div>
          )}
        </EditorLayout>
      </MainLayout>
      <ConfirmSaveLegalDocumentModal />
    </>
  );
};

export default PrivacyPolicy;
